import { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { useOktaAuth } from '@okta/okta-react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import { setSource } from '@/app/external/ExternalActions';
import { LoadingMagicLink } from '@/app/external/LoadingMagicLink';
import { MagicLinkErrorPage } from '@/app/external/MagicLinkErrorPage';
import { FRENCH_LOCALES } from '@/locales/helpers';
import { PatientBenefitsCombinedPage } from '@/modules/autoVerify/benefitsPage/PatientBenefitsCombinedPage';
import { ClaimsPage } from '@/modules/claims/ClaimsPage';
import { Insights } from '@/modules/insights/insights/Insights';
import { PracticeGroups } from '@/modules/insights/practiceGroups/PracticeGroups';
import { isMagicLinkUserAtom } from '@/modules/login/atoms';
import { ReferralsPage } from '@/modules/referrals/ReferralsPage';
import { isExternalIFrame } from '@/utils/iframe';

import AboutRouter from '../app/about/AboutRouter';
import App from '../app/App';
import { selectedVisitAtom } from '../app/appHeader/imageViewerMenu/atoms';
import AppLoader from '../app/AppLoader';
import { selectedViewAtom } from '../app/external/atoms';
import { ExternalModalHandler } from '../app/external/ExternalModal/ExternalModalHandler';
import { ExternalUrlHandler } from '../app/external/ExternalUrlHandler';
import { ExternalVisitSidebarHandler } from '../app/external/ExternalVisitSidebar/ExternalVisitSidebarHandler';
import { PatientDownloadableHandler } from '../app/external/patientDownloadable/PatientDownloadableHandler';
import { PatientDownloadableLinkHandler } from '../app/external/patientDownloadable/PatientDownloadableLinkHandler';
import { HydrateUser } from '../app/HydrateUser';
import {
  displayThresholdMaxAtom,
  externalPmsSourceAtom,
  imageTimestampAtom,
  imageViewAtom,
  practiceImageIdAtom,
  practicePatientIdAtom,
  redirectToSwitchBoardAtom,
  videaPatientIdAtom,
} from '../atoms/externalAtoms';
import WithAxios from '../common-components/axios/WithAxios';
import VideaLogoSm from '../common-components/elements/icon/svgs/VideaBrandSm';
import { startMswForCurrentEnv } from '../mocks';
import { AmbientPage } from '../modules/ambient/AmbientPage';
import AnnotatorContainer from '../modules/annotator/AnnotatorContainer';
import { showSegmentationFromEmbeddedAtom } from '../modules/annotator/multiFileViewer/imageToolbar/atoms';
import { AutoVerifyPage } from '../modules/autoVerify';
import { ClaimSummaryPage } from '../modules/claims/claim/generateClaimSummaryPage/ClaimSummaryPage';
import { DDAIMetrics } from '../modules/ddaiMetrics/DDAIMetrics';
import { LandingPage } from '../modules/landingPage/LandingPage';
import IdpLogin from '../modules/login/IdpLogin';
import Login from '../modules/login/Login';
import {
  hasNoValidLocations,
  isMultiPracticeUser,
} from '../modules/login/login-utils';
import { Onboarding } from '../modules/onboarding/Onboarding';
import OpDashboard from '../modules/opDashboard/OpDashboard';
import { PatientList } from '../modules/patientList/PatientList';
import { useShouldHideHuddle } from '../modules/patientList/useShouldHideHuddle';
import { CheckForHistoricalImages } from '../modules/processHistorical/CheckForHistoricalImages';
import {
  paramsAtom,
  selectedDashboardAtom,
} from '../modules/simplifiedInsights/atoms';
import { SimplifiedInsights } from '../modules/simplifiedInsights/SimplifiedInsights';
import { Trial } from '../modules/trial/Trial';
import oktaConfig from '../oktaConstants';
import { usePreferences } from '../react-query/usePreferences';
import { blueBlack400, greyLight500 } from '../theme/videa-style-tokens';
import { useDevtoolsShortcut } from '../utils/hooks/useDevtoolsShortcut';
import {
  useDirectorPracticeView,
  useDirectorPracticeViewState,
} from '../utils/hooks/useDirectorPracticeView';
import { useOnboarding } from '../utils/hooks/useOnboarding';
import { useTrackPageView } from '../utils/hooks/useTrackPageView';
import { mixpanelEvents, useTrackEvent } from '../utils/mixpanel';
import {
  FeatureFlagType,
  OrganizationFeatureFlagKey,
  PracticeFeatureFlagKey,
  useFeatureFlag,
} from '../utils/user';

import {
  IncipientContext,
  defaultIncipientThresholdRange,
} from './IncipientContext';
import routes, { envToSalesDemoAccount } from './routes.constants';

const ViewerError = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: black;
  color: ${blueBlack400};
  flex-direction: column;
  box-sizing: border-box;
  padding: 4em;
  gap: 1em;

  .videa-logo {
    width: 10em;
    height: auto;
  }

  .error-title {
    display: block;
    font-size: 2em;
    margin-bottom: 1em;
    font-weight: 400;
    font-family: 'Neue Haas Grotesk Display Pro', sans-serif;
    color: ${greyLight500};
  }
  .error-message {
    display: block;
    font-size: 1em;
    font-family: 'Neue Haas Grotesk Display Pro', sans-serif;

    a {
      color: ${blueBlack400};
    }
  }
`;

const Error = function Error(props: {
  location: { state: { message: string } };
}) {
  const errorMessage =
    props?.location?.state?.message || 'Unexpected error occurred';
  return (
    <h3>
      <Trans>Error: {errorMessage}</Trans>
    </h3>
  );
};

const PatientMatchError = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state: any) => state?.login?.user);
  const hideHuddle = useShouldHideHuddle();

  // Show back button if not in embedded mode (URL doesn't start with /embedded)
  const isEmbedded = location.pathname.includes('/embedded');

  // Check if user is a director with no practice ID
  const hasNoPracticeId = !user?.primaryPracticeId;
  const canViewMultiplePractices = isMultiPracticeUser(user);
  const shouldShowSwitchboard = hasNoPracticeId && canViewMultiplePractices;

  const handleBackClick = () => {
    if (shouldShowSwitchboard) {
      history.push(routes.SWITCHBOARD);
    } else {
      history.push(routes.UPLOADER);
    }
  };

  const backButtonText = shouldShowSwitchboard ? (
    <Trans>Back to Practice List</Trans>
  ) : hideHuddle ? (
    <Trans>All Patients</Trans>
  ) : (
    <Trans>Dashboard</Trans>
  );

  return (
    <ViewerError>
      <VideaLogoSm />
      <div className="error-title">
        <Trans>No AI findings are available for the selected patient</Trans>
      </div>
      <div className="error-message">
        <Trans>
          Visit{' '}
          <a href="https://help.videa.ai" target="_blank" rel="noreferrer">
            help.videa.ai
          </a>{' '}
          for help, or please contact your administrator for assistance.
        </Trans>
      </div>
      {!isEmbedded && (
        <div style={{ marginTop: '2em' }}>
          <button
            onClick={handleBackClick}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '0.375em',
              padding: '0.75em 1.5em',
              fontSize: '1em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {backButtonText}
          </button>
        </div>
      )}
    </ViewerError>
  );
};

const UnauthorizedContainer = styled.div`
  padding: 4em;
`;

const Unauthorized = function Unauthorized() {
  return (
    <UnauthorizedContainer>
      <h3>
        <Trans>
          It appears you don&apos;t have permission to access this resource, or
          you have not been assigned access to any practices. If you are looking
          for the VideaAI Learning Center, go to{' '}
          <a href="https://help.videa.ai">help.videa.ai</a>. Otherwise, please
          contact your administrator for assistance.
        </Trans>
      </h3>
    </UnauthorizedContainer>
  );
};

const ProtectedRoute = ({ user, component, ...rest }: any) => {
  const practicePatientId = useAtomValue(practicePatientIdAtom);
  const practiceImageId = useAtomValue(practiceImageIdAtom);
  const selectedView = useAtomValue(selectedViewAtom);
  const shouldDisplayThresholdMax = useAtomValue(displayThresholdMaxAtom);
  const imageView = useAtomValue(imageViewAtom);
  const shouldRedirectToSwitchBoard = useAtomValue(redirectToSwitchBoardAtom);
  const [externalPmsSource, setExternalPmsSource] = useAtom(
    externalPmsSourceAtom,
  );
  const [videaPatientId, setVideaPatientId] = useAtom(videaPatientIdAtom);
  const setSelectedVisit = useSetAtom(selectedVisitAtom);

  const showSegmentationFromEmbedded = useAtomValue(
    showSegmentationFromEmbeddedAtom,
  );
  const { isDirectorPracticeViewUser } = useDirectorPracticeView();
  const { authState } = useOktaAuth();
  const history = useHistory();
  const { data: prefs } = usePreferences();

  const location = useLocation();
  const showOpDashboard = !!useFeatureFlag(
    OrganizationFeatureFlagKey.INSIGHTS_DASHBOARD_ENABLED,
    FeatureFlagType.ORGANIZATION,
  )?.value;

  const directorRoute = showOpDashboard
    ? routes.VIDEA_INSIGHT
    : routes.SWITCHBOARD;

  const setSelectedDashboard = useSetAtom(selectedDashboardAtom);
  const setParams = useSetAtom(paramsAtom);

  const { path } = rest;

  const isLoggedIn = authState && authState.isAuthenticated;

  // We might hit a link from an external application directly to a protected route.
  // Such as in the case of tableau -> fmx view
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const source = urlParams.get('source');
  useEffect(() => {
    if (source && !externalPmsSource) {
      setExternalPmsSource(source);
    }
  }, [externalPmsSource, setExternalPmsSource, source]);

  useEffect(() => {
    const visitId = urlParams.get('visit-id');
    if (source === 'insights' && visitId) {
      setSelectedVisit(visitId);
    }
  }, [setExternalPmsSource, setSelectedVisit, source, urlParams]);

  useEffect(() => {
    // If coming from tableau, save the videa patient id in case we log out and need to persist the selected patient
    if (
      source === 'insights' &&
      !isLoggedIn &&
      location.pathname.includes(routes.ANNOTATOR)
    ) {
      setVideaPatientId(
        location.pathname.substring(location.pathname.lastIndexOf('/') + 1),
      );
    }
  }, [isLoggedIn, location, setVideaPatientId, source]);

  const isDentalDirector = useMemo(
    () =>
      user && isLoggedIn && isMultiPracticeUser(user) && path !== directorRoute,

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useMemo has a missing dependency: 'directorRoute'. Either include it or remove the dependency array. */
    [user, isLoggedIn, path],
  );

  useEffect(() => {
    if (!path.includes(routes.SWITCHBOARD)) {
      setSelectedDashboard(undefined);
      setParams(undefined);
    }
  }, [path, setParams, setSelectedDashboard]);

  const isDirectorScreen =
    path.includes(routes.SWITCHBOARD) ||
    path.includes(routes.VIDEA_INSIGHT) ||
    path.includes(routes.ANNOTATOR) ||
    path.includes(routes.INSIGHTS) ||
    path.includes(routes.PRACTICE_GROUPS);

  const isProtectedRoute =
    path.includes(routes.ERROR) ||
    path.includes(routes.PATIENT_MATCH_ERROR) ||
    path.includes(routes.UPLOADER) ||
    path.includes(routes.CLAIMS) ||
    path.includes(routes.ANNOTATOR) ||
    path.includes(routes.VIDEA_INSIGHT) ||
    path.includes(routes.ABOUT) ||
    path.includes(directorRoute) ||
    path.includes(routes.INSURANCE_EXPORT) ||
    path.includes(routes.TRIAL) ||
    path.includes(routes.INSIGHTS) ||
    path.includes(routes.PRACTICE_GROUPS) ||
    path.includes(routes.REFERRALS) ||
    path.includes(routes.AMBIENT);

  if (externalPmsSource === 'insights' && isLoggedIn && videaPatientId) {
    setVideaPatientId('');
    return <Redirect to={`${routes.ANNOTATOR}/${videaPatientId}`} />;
  }

  // Note: When there is no user object available any more, the app resets to the login page
  if (isLoggedIn !== null && isLoggedIn === false && isProtectedRoute) {
    return <Redirect to={routes.LOGIN} />;
  }

  if (isLoggedIn && hasNoValidLocations(user)) {
    return <Redirect to={routes.UNAUTHORIZED} />;
  }

  if (
    user &&
    isLoggedIn &&
    practicePatientId &&
    !path.includes(routes.ANNOTATOR) &&
    !shouldRedirectToSwitchBoard
  ) {
    history.push(`${
      routes.EXTERNAL
    }?patientid=${practicePatientId}&imageid=${practiceImageId}&
      display-threshold-max=${shouldDisplayThresholdMax}&view=${selectedView}&source=${externalPmsSource}&defaultView=${
        showSegmentationFromEmbedded ? 'patient-view' : 'findings'
      }&imageView=${imageView}`);
    return;
  }

  // check the role for operation dashboard
  if (
    user &&
    isLoggedIn &&
    path.includes(directorRoute) &&
    !isMultiPracticeUser(user)
  ) {
    return <Redirect to={routes.UPLOADER} />;
  }

  if (user && isLoggedIn && isDentalDirector && !isDirectorPracticeViewUser) {
    if (
      // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.

      envToSalesDemoAccount[process.env.VITE_ENV].primaryOrganizationId ===
      user.primaryOrganizationId
    ) {
      return (
        <Redirect
          to={`${routes.UPLOADER}?director_view_practice_id=${
            // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.

            envToSalesDemoAccount[process.env.VITE_ENV].primaryPracticeId
          }`}
        />
      );
    }

    if (!isDirectorScreen) {
      return <Redirect to={directorRoute} />;
    }
  }

  // If standaloneClaimsEnabled is true and trying to access blocked routes, redirect to claims
  if (
    isLoggedIn &&
    prefs?.standaloneClaimsEnabled &&
    prefs?.cleanClaims &&
    (path.includes(routes.UPLOADER) ||
      path.includes(routes.VIDEA_INSIGHT) ||
      path.includes(routes.INSIGHTS) ||
      path.includes(routes.AMBIENT))
  ) {
    return <Redirect to={routes.CLAIMS} />;
  }

  // If voice notes standalone is true and trying to access blocked routes, redirect to voice notes standalone
  if (
    isLoggedIn &&
    prefs?.enableVoiceNotesStandalone &&
    (path.includes(routes.UPLOADER) ||
      path.includes(routes.CLAIMS) ||
      path.includes(routes.REFERRALS))
  ) {
    return <Redirect to={routes.AMBIENT} />;
  }

  return <Route {...rest} component={component} />;
};

const RedirectRoute = ({ ...rest }: any) => {
  const { authState } = useOktaAuth();

  const isLoggedIn = authState?.isAuthenticated;
  const location = useLocation();
  const dispatch = useDispatch();
  const urlParams = new URLSearchParams(location.search);

  const { trackEvent } = useTrackEvent();

  const isMagicLinkUser = useAtomValue(isMagicLinkUserAtom);
  const isExternalIframe = isExternalIFrame();

  const setSelectedView = useSetAtom(selectedViewAtom);
  const setPracticePatientId = useSetAtom(practicePatientIdAtom);
  const setPracticeImageId = useSetAtom(practiceImageIdAtom);
  const setDisplayThresholdMax = useSetAtom(displayThresholdMaxAtom);
  const setImageView = useSetAtom(imageViewAtom);
  const setExternalPmsSource = useSetAtom(externalPmsSourceAtom);
  const setImageTimestamp = useSetAtom(imageTimestampAtom);
  const setShowSegmentationFromEmbedded = useSetAtom(
    showSegmentationFromEmbeddedAtom,
  );

  // Incorrect instructions were given to a few practices on building the URL - accept both casing
  const patientId = urlParams.get('patientid') || urlParams.get('patientId');
  const imageId = urlParams.get('imageid');
  const displayThresholdMax = urlParams.get('display-threshold-max');
  const view = urlParams.get('view');
  const externalPmsSource = urlParams.get('source');
  const imageView = urlParams.get('imageView');
  const defaultView = urlParams.get('default-view');
  const timestamp = urlParams.get('timestamp');

  // DDAI users do not have a source currently, this will need to be updated once the DDAI source is added
  const isDDAIUser = patientId && imageId && !externalPmsSource;

  useEffect(() => {
    if (!isExternalIframe) {
      // For iFrame, we track this event lower in the component tree
      trackEvent(mixpanelEvents.externalPmsEntry, {
        externalPmsSource,
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'externalPmsSource' and 'trackEvent'. Either include them or remove the dependency array. */
  }, []);

  if (!patientId) {
    return <Route {...rest} />;
  }

  setPracticePatientId(patientId);
  if (imageId) {
    setPracticeImageId(imageId);
  } else {
    setPracticeImageId('');
  }
  if (displayThresholdMax) {
    setDisplayThresholdMax(displayThresholdMax === 'true');
  }
  if (imageId && imageView) {
    setImageView(imageView);
  }
  if (view) {
    setSelectedView(view);
  }
  if (isDDAIUser) {
    dispatch(setSource('DDAI'));
  }
  if (defaultView === 'patient-view') {
    setShowSegmentationFromEmbedded(true);
  } else {
    setShowSegmentationFromEmbedded(false);
  }
  if (externalPmsSource) {
    setExternalPmsSource(externalPmsSource);
  }
  if (timestamp) {
    setImageTimestamp(timestamp);
  }
  // Currently imageId should only be defined if they are an external user coming from DDAI
  if (!isLoggedIn && isDDAIUser) {
    if (isMagicLinkUser) {
      trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
        cause: 'DDAI unauthenticated redirect',
      });
    }
    return <Redirect to={routes.LANDING_PAGE} />;
  }
  if (!isLoggedIn) {
    if (isMagicLinkUser) {
      trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
        cause: 'Routes non-DDAI unauthenticated redirect',
      });
    }
    return <Redirect to={routes.LOGIN} />;
  }

  return <Route {...rest} />;
};

RedirectRoute.propTypes = {
  user: PropTypes.object,
  authState: PropTypes.any,
};

const OktaLoginContainer = () => {
  const { i18n, t } = useLingui();
  return (
    <Login
      config={{
        baseUrl: oktaConfig.baseUrl,
        clientId: oktaConfig.oidc.clientId,
        redirectUri: oktaConfig.oidc.redirectUri,
        logo: '/logo.svg',
        i18n: {
          en: {
            'primaryauth.title': t`Sign in to VideaAI`,
          },
          fr: {
            'primaryauth.title': t`Sign in to VideaAI`,
          },
        },
        useClassicEngine: oktaConfig.useClassicEngine,
        language: FRENCH_LOCALES.includes(i18n.locale) ? 'fr' : 'en',
        assets: {
          baseUrl: '/node_modules/@okta/okta-signin-widget/dist',
        },
      }}
    />
  );
};

OktaLoginContainer.propTypes = {
  history: PropTypes.any,
};

const OktaSSOLoginContainer = () => {
  const { i18n, t } = useLingui();

  return (
    <Login
      config={{
        baseUrl: oktaConfig.baseUrl,
        clientId: oktaConfig.oidc.clientId,
        redirectUri: oktaConfig.oidc.redirectUri,
        logo: '/logo.svg',
        i18n: {
          en: {
            'primaryauth.title': t`Sign in to VideaAI`,
          },
          fr: {
            'primaryauth.title': t`Sign in to VideaAI`,
          },
        },
        idpDisplay: 'SECONDARY',
        features: {
          idpDiscovery: true,
        },
        idpDiscovery: {
          requestContext: window.location.href,
        },
        useClassicEngine: oktaConfig.useClassicEngine,
        language: FRENCH_LOCALES.includes(i18n.locale) ? 'fr' : 'en',
        assets: {
          baseUrl: '/node_modules/@okta/okta-signin-widget/dist',
        },
      }}
    />
  );
};

const Routes = (props: any) => {
  const { user, history } = props;
  const { authState } = useOktaAuth();
  const showOnboarding = useOnboarding();
  const { data: prefs } = usePreferences();
  useDirectorPracticeViewState();
  useTrackPageView();
  const isExternalIframeUser = isExternalIFrame();

  // Get RBL tool state for React Query DevTools
  const { showDevtools } = useDevtoolsShortcut();

  const [incipientThresholdRange, setIncipientThresholdRange] = useState(
    defaultIncipientThresholdRange,
  );

  const IncipientContextProviderValue = useMemo(
    () => ({
      incipientThresholdRange,
      setIncipientThresholdRange,
    }),
    [incipientThresholdRange, setIncipientThresholdRange],
  );

  const isSalesDemoMockEnabled = !!useFeatureFlag(
    OrganizationFeatureFlagKey.SALES_DEMO_INSIGHTS_MOCK_ENABLED,
    FeatureFlagType.ORGANIZATION,
  )?.value;

  const isSalesDemo = !!useFeatureFlag(
    PracticeFeatureFlagKey.IS_SALES_DEMO,
    FeatureFlagType.PRACTICE,
  )?.value;

  useEffect(() => {
    const allowedEnvs = ['local', 'development', 'test'];
    //If not the sales demo and not local/dev environments we should not start MSW
    if (!process.env.VITE_ENV || !allowedEnvs.includes(process.env.VITE_ENV)) {
      return;
    }
    startMswForCurrentEnv();
  }, [isSalesDemoMockEnabled, isSalesDemo]);

  return (
    <>
      <HydrateUser>
        <WithAxios>
          <Switch>
            <Route
              component={PatientDownloadableLinkHandler}
              path={routes.PUBLIC}
            />
            <Route
              component={PatientDownloadableHandler}
              path={routes.PATIENT_DOWNLOADABLE}
            />
            <Route component={LoadingMagicLink} path={routes.MAGIC} />

            <App
              isLoggedIn={!!authState?.isAuthenticated}
              user={user}
              history={history}
            >
              <IncipientContext.Provider value={IncipientContextProviderValue}>
                {showOnboarding && authState?.isAuthenticated && <Onboarding />}
                <Switch>
                  <ProtectedRoute
                    component={DDAIMetrics}
                    path={routes.ANALYTICS}
                    user={user}
                  />

                  <Route component={LoadingMagicLink} path={routes.MAGIC} />
                  <RedirectRoute
                    // @ts-expect-error TS(2322): Type '{ path: "/external-visit-sidebar"; user: any; authState: A... Remove this comment to see the full error message
                    path={routes.EXTERNAL_VISIT_SIDEBAR}
                    user={user}
                    component={ExternalVisitSidebarHandler}
                  />
                  <RedirectRoute
                    // @ts-expect-error TS(2322): Type '{ path: "/external-modal"; user: any; authState: A... Remove this comment to see the full error message
                    path={routes.EXTERNAL_MODAL}
                    user={user}
                    component={ExternalModalHandler}
                  />
                  <RedirectRoute
                    // @ts-expect-error TS(2322): Type '{ path: "/external"; user: any; authState: A... Remove this comment to see the full error message
                    path={routes.EXTERNAL}
                    user={user}
                    component={ExternalUrlHandler}
                  />

                  <ProtectedRoute
                    component={PatientList}
                    path={routes.UPLOADER}
                    user={user}
                  />

                  {prefs.cleanClaims && (
                    <ProtectedRoute
                      component={ClaimSummaryPage}
                      path={`${routes.CLAIMS}${routes.CLAIM_SUMMARY}`}
                      user={user}
                    />
                  )}

                  {prefs.cleanClaims && (
                    <ProtectedRoute
                      component={ClaimsPage}
                      path={routes.CLAIMS}
                      user={user}
                    />
                  )}

                  {prefs?.isInsuranceEligibilityEnabled && (
                    <ProtectedRoute
                      component={PatientBenefitsCombinedPage}
                      path={routes.PATIENT_BENEFITS}
                      user={user}
                    />
                  )}

                  {prefs?.isInsuranceEligibilityEnabled && (
                    <ProtectedRoute
                      component={AutoVerifyPage}
                      path={routes.AUTOVERIFY}
                      user={user}
                    />
                  )}

                  {prefs?.isVideaNotesEnabled && (
                    <ProtectedRoute
                      component={AmbientPage}
                      path={routes.AMBIENT}
                      user={user}
                    />
                  )}

                  <ProtectedRoute
                    component={ReferralsPage}
                    path={routes.REFERRALS}
                    user={user}
                  />

                  <ProtectedRoute
                    component={CheckForHistoricalImages}
                    path={`${routes.HISTORICAL}`}
                    user={user}
                  />

                  <ProtectedRoute
                    component={AnnotatorContainer}
                    path={`/embedded${routes.ANNOTATOR}/:id/:imageId`}
                    user={user}
                  />

                  <ProtectedRoute
                    component={AnnotatorContainer}
                    path={`${routes.ANNOTATOR}/:id/:imageId`}
                    user={user}
                  />

                  <ProtectedRoute
                    component={AnnotatorContainer}
                    path={`${routes.ANNOTATOR}/:id`}
                    user={user}
                  />

                  <ProtectedRoute
                    component={AnnotatorContainer}
                    path={`/embedded${routes.ANNOTATOR}/:id`}
                    user={user}
                  />

                  <ProtectedRoute
                    component={OpDashboard}
                    path={routes.VIDEA_INSIGHT}
                    user={user}
                  />

                  <ProtectedRoute
                    component={SimplifiedInsights}
                    path={routes.SWITCHBOARD}
                    user={user}
                  />

                  <ProtectedRoute
                    component={Insights}
                    path={routes.INSIGHTS}
                    user={user}
                  />

                  {prefs.enableCustomPracticeGroups && (
                    <ProtectedRoute
                      component={PracticeGroups}
                      path={routes.PRACTICE_GROUPS}
                      user={user}
                    />
                  )}

                  <Route
                    component={
                      isExternalIframeUser
                        ? MagicLinkErrorPage
                        : OktaSSOLoginContainer
                    }
                    path={routes.LOGIN}
                  />

                  <Route
                    component={OktaLoginContainer}
                    path={routes.CLASSIC_LOGIN}
                    // @ts-expect-error TS(2322): Type '{ path: "/external"; user: any; authState: A... Remove this comment to see the full error message
                    user={user}
                  />

                  <Route component={IdpLogin} path={routes.IDP_LOGIN} />
                  <Route component={Error} path={routes.ERROR} />
                  <Route
                    component={PatientMatchError}
                    path={routes.PATIENT_MATCH_ERROR}
                  />
                  <Route
                    component={PatientMatchError}
                    path={`/embedded${routes.PATIENT_MATCH_ERROR}`}
                  />

                  <Route component={Unauthorized} path={routes.UNAUTHORIZED} />

                  <ProtectedRoute
                    component={AboutRouter}
                    path={routes.ABOUT}
                    history={history}
                  />

                  <ProtectedRoute
                    component={AboutRouter}
                    path={`/embedded${routes.ABOUT}`}
                    history={history}
                  />

                  <Route component={LandingPage} path={routes.LANDING_PAGE} />

                  <ProtectedRoute
                    component={Trial}
                    path={routes.TRIAL}
                    history={history}
                  />

                  <Route component={AppLoader} />
                </Switch>
              </IncipientContext.Provider>
            </App>
          </Switch>
        </WithAxios>
      </HydrateUser>
      {/* React Query DevTools - only show when RBL tool is activated */}
      {showDevtools && (
        <ReactQueryDevtools initialIsOpen={false} position="top-right" />
      )}
    </>
  );
};

Routes.propTypes = {
  user: PropTypes.object,
  trackFatalError: PropTypes.func,
  history: PropTypes.object,
};

export default Routes;
